/**
 * Formatter: Date
 */
import { DateTime, Duration, Info } from 'luxon'

/**
 * This method will convert date string or number to DateTime
 * @param date
 * @param zoneString
 */
export const convertToDate = (date: string | number, zoneString: string) => {
  if (typeof date === 'string') {
    if (date.length === 10) {
      return DateTime.fromSQL(date)
    }
    if (date.length > 10) {
      return DateTime.fromISO(date, { zone: zoneString })
    }
  } else if (typeof date === 'number') {
    return DateTime.fromMillis(date, { zone: zoneString })
  }
  return null
}

/**
 * This Object accepts an ISO, SQL, or unix date and returns the formatted date.
 * This will also handle the time zone issue for IE-11/Older iPad convert date time using the UTC timezone.
 * UTC-4 work for the EDT(Eastern Daylight savings time)
 * UTC-5 Work for the EST(Eastern Standard Time)
 * @param date - The date to format
 * @param format - The output format of the date
 */
export const FormatDate = (date: string | number, format: string) => {
  let zone: string
  if (Info.features().zones === false) {
    zone = 'UTC-4'
  } else {
    zone = 'America/New_York'
  }
  const dateTime = convertToDate(date, zone)
  if (dateTime === null) return ''
  return dateTime.toFormat(format)
}

/**
 * Accepts an ISO, SQL, or unix date and returns it in a localized format.
 * If the user has selected a non-English language, this function will select
 * a localized format corresponding to the provided default format
 * @param date - The date to format
 * @param defaultFormat - The output format to use if the selected language
 * is English
 * @param locale - Optional. The locale in which to format the date. If omitted,
 * use English
 * @returns the date in a localized format
 */
export const formatDateLocalized = (
  date: string | number,
  format: string,
  locale?: string
) => {
  // RWR-3316: if language is not English, use yyyy-MM-dd format
  const localizedFormat =
    locale === undefined || locale === 'en' ? format : 'yyyy-MM-dd'
  return FormatDate(date, localizedFormat)
}

/**
 * This will convert the Date string or number to the Formatted DateString and append the ET.
 * @param date
 */
export const FormatDateET = (date: string | number) => {
  const dateTime = FormatDate(date, 'ccc dd MMM, yyyy - t')
  if (dateTime === null) return ''
  return dateTime.concat(' ET')
}

/**
 * This will convert the miliseconds to HH:MM:SS format
 * @param duration
 */
export const convertMilliSeconds = (duration: number, format = 'hh:mm:ss') =>
  Duration.fromObject({ milliseconds: duration }).toFormat(format)

const ISO_DATE = /^\d\d\d\d-\d\d-\d\d$/
/**
 * Converts a date-only value to a timestamp with time of midnight
 * in Eastern Time (UTC-0400 in summer, UTC-0500 in winter)
 * @param date
 * @returns A ISO-formatted timestamp.
 */
export const dateToTimestampMidnightET = (date: string | any) => {
  if (ISO_DATE.test(date)) {
    const zone = 'America/New_York'
    const datetime = DateTime.fromSQL(date, { zone })
    return datetime.toUTC().toISO()
  }
  return date
}
